// The curated op allowlist for the chainable shadow proxy (M3.4, WORK-6305).
//
// `02-wrapper-design.md` § "API surface" promised "an explicit pass-through
// allowlist" so the set of instrumented vs. delegated Moment methods is greppable
// rather than implied by omission. This file is that allowlist, made real.
//
// Instrumenting *every* Moment method would multiply the metrics write volume
// (01-metrics-tooling.md) by the average chain length and bury real DST /
// end-of-month / parse-strict signal under low-risk ops (`.toObject()`,
// `.creationData()`). Instead we instrument a curated, high-risk surface — every
// moment instance method actually used across app/javascript (usage-scan derived)
// — and explicitly delegate the rest.
//
// Classes:
//   CHAINING  — produces a new date-like value. The proxy compares the produced
//               instant (via `.format()`) and returns a re-wrapped pair so the
//               NEXT hop is shadowed too.
//   TERMINAL  — produces a scalar / string / boolean. The proxy compares the
//               serialized output and returns the raw Moment scalar.
//   DUAL      — getter/setter methods (`year`/`hour`/…) that return a number with
//               no arg (terminal) and set + return a Moment with an arg (chaining).
//               Classified by arity at call time, never by name alone.
//   REWRAP    — identity-preserving ops (`clone`) that must re-wrap the pair so
//               downstream chains stay shadowed, but carry no meaningful
//               comparison — emitting a counter for them would be pure noise.
//   PASS_THROUGH — everything else: delegated to the real Moment with no
//               comparison. Two reasons land an op here:
//                 1. low-risk / structural ops with nothing date-like to diff
//                    (`toObject`, `toArray`, `toDate`, `isValid`, `locale`, …);
//                 2. ops with no dayjs peer in the canonical datetime service —
//                    most notably `isBetween` (its dayjs isBetween plugin is not
//                    loaded there), which therefore CANNOT be compared. Listing it
//                    keeps the gap explicit instead of silent.

export const CHAINING_OPS = new Set<string>([
  'add',
  'subtract',
  'startOf',
  'endOf',
  'utc',
  'local',
  'tz',
  'set',
]);

export const TERMINAL_OPS = new Set<string>([
  'format',
  'valueOf',
  'unix',
  'toISOString',
  'diff',
  'isBefore',
  'isAfter',
  'isSame',
  'isSameOrBefore',
  'isSameOrAfter',
]);

// Dual getter/setter methods — classified by arity at call time (see classifyOp).
export const DUAL_OPS = new Set<string>([
  'year',
  'month',
  'date',
  'day',
  'weekday',
  'hour',
  'hours',
  'minute',
  'minutes',
  'second',
  'seconds',
  'millisecond',
  'milliseconds',
]);

// Identity-preserving ops: re-wrap the pair (so the chain stays shadowed) but
// emit no comparison counter.
export const REWRAP_ONLY = new Set<string>(['clone']);

// Explicit pass-through allowlist. The unlisted default also passes through; this
// set names the ops a reader is most likely to expect to be instrumented but that
// deliberately are not, with the reason.
export const PASS_THROUGH = new Set<string>([
  'isValid',
  'toDate',
  'toObject',
  'toArray',
  'toJSON',
  'locale',
  'creationData',
  'fromNow',
  'calendar',
  // No dayjs isBetween plugin is loaded in the canonical datetime service, so
  // there is no dayjs peer to compare against — short-circuit per
  // 02-wrapper-design.md.
  'isBetween',
]);

// dayjs has no plural getter/setter aliases (`.hours()` / `.minutes()` /
// `.seconds()` / `.milliseconds()` are moment-only). The values are identical to
// their singular peers, so when the proxy drives the dayjs side for a plural op it
// invokes the singular method instead, preserving parity.
// Null-prototype so a lookup for an inherited Object method name (`valueOf`,
// `toString`, `constructor`, …) returns undefined instead of the prototype
// function — those are real ops and must fall through to themselves, not be
// rewritten to `Object.prototype.valueOf`.
export const DAYJS_METHOD_ALIAS: Readonly<Record<string, string>> =
  Object.freeze(
    Object.assign(Object.create(null) as Record<string, string>, {
      hours: 'hour',
      minutes: 'minute',
      seconds: 'second',
      milliseconds: 'millisecond',
    })
  );

export type OpClassType = 'chaining' | 'terminal' | 'rewrap' | 'pass-through';

export function classifyOp(op: string, argCount: number): OpClassType {
  if (DUAL_OPS.has(op)) {
    return argCount === 0 ? 'terminal' : 'chaining';
  }
  if (REWRAP_ONLY.has(op)) {
    return 'rewrap';
  }
  if (CHAINING_OPS.has(op)) {
    return 'chaining';
  }
  if (TERMINAL_OPS.has(op)) {
    return 'terminal';
  }
  return 'pass-through';
}
